import React, { useState } from "react";
import { checkEligibility } from "../api/medikredit/eligibilityApi";
import styles from "../css/components/EligibilityButton.module.css";

export default function EligibilityButton({ patientId, fields, onResult }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const canCheck = patientId
    ? true
    : !!(fields?.medicalAidNumber && fields?.optioncodeid && fields?.surname);

  async function run() {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const data = patientId
        ? await checkEligibility({ patientId })
        : await checkEligibility(fields);
      setResult(data);
      if (onResult) onResult(data);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Eligibility check failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.btn}
        onClick={run}
        disabled={loading || !canCheck}
        title={!canCheck ? "Enter medical aid number and select a scheme first" : undefined}
      >
        {loading ? (
          <>
            <span className={styles.spinner} />
            Checking…
          </>
        ) : (
          <>
            <ShieldIcon className={styles.icon} />
            Check Eligibility
          </>
        )}
      </button>

      {result && result.approved && (
        <div className={`${styles.result} ${styles.approved}`}>
          <CheckIcon className={styles.resultIcon} />
          <div className={styles.resultBody}>
            <span className={styles.resultTitle}>Member valid</span>
            {result.authNumber && (
              <span className={styles.authChip}>Switch ref (Hnet) · {result.authNumber}</span>
            )}
            {result.patients && result.patients.length > 0 && (
              <ul className={styles.reasons}>
                {result.patients.map((p, i) => (
                  <li key={i}>
                    {p.firstName || p.surname
                      ? `${p.firstName || ""} ${p.surname || ""}`.trim()
                      : `Dependant ${p.depCode || i + 1}`}
                    {p.status ? ` — ${p.status}` : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {result && !result.approved && (
        <div className={`${styles.result} ${styles.rejected}`}>
          <CrossIcon className={styles.resultIcon} />
          <div className={styles.resultBody}>
            <span className={styles.resultTitle}>Not valid on this scheme</span>
            {result.authNumber && (
              <span className={styles.authChip}>Switch ref (Hnet) · {result.authNumber}</span>
            )}
            {result.patients && result.patients.length > 0 && (
              <ul className={styles.reasons}>
                {result.patients.map((p, i) => (
                  <li key={i}>
                    {p.firstName || p.surname
                      ? `${p.firstName || ""} ${p.surname || ""}`.trim()
                      : `Dependant ${p.depCode || i + 1}`}
                    {p.status ? ` — ${p.status}` : ""}
                  </li>
                ))}
              </ul>
            )}
            {result.messages && result.messages.length > 0 && (
              <ul className={styles.reasons}>
                {result.messages.map((m, i) => (
                  <li key={i}>
                    {m.desc} <span className={styles.code}>({m.code})</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {error && (
        <div className={`${styles.result} ${styles.errorBox}`}>
          <WarnIcon className={styles.resultIcon} />
          <div className={styles.resultBody}>
            <span className={styles.resultTitle}>Couldn’t complete check</span>
            <span>{error}</span>
          </div>
        </div>
      )}
    </div>
  );
}

/* --- inline icons (no extra deps) --- */
function ShieldIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  );
}
function CheckIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  );
}
function CrossIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </svg>
  );
}
function WarnIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  );
}
